import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Divider,
  Grid,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Tabs,
  Tab,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
  IconButton,
  Alert,
  LinearProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ButtonBase } from "@mui/material";
import { IconEdit, IconTrash, IconPlus, IconX, IconFileText, IconPhoto, IconUpload, IconDownload } from "@tabler/icons-react";
import MainCard from "ui-component/cards/MainCard";
import SearchSection from "layout/MainLayout/Header/SearchSection";
import useViaCep from "hooks/useViaCep";
import useContractPermissions from "hooks/useContractPermissions";
import TabPanel from "components/ui/tabs/TabPanel";
import CustomerNameButton from "components/customer360/CustomerNameButton";
import { MIN_CONTRACT_DATE, getMaxTodayDate } from "utils/date-limits";

const formatDateBR = (isoDate) => {
  if (!isoDate) return "—";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}/${month}/${year}`;
};

const EditButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const DeleteButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.error.dark,
  "&:hover": {
    background: theme.palette.error.dark,
    color: theme.palette.error.light,
  },
  width: 40,
  height: 40,
  borderRadius: 12,
  cursor: "pointer",
}));

const PrintButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const FilesButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const AddButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  align: "center",
  gap: 8,
  width: 150,
  height: 40,
  borderRadius: 10,
  display: "flex",
  cursor: "pointer",
}));

const CustomersList = () => {
  const { fetchAddress, loading: cepLoading, error: cepError } = useViaCep();
  const { canImportData } = useContractPermissions();
  const [customers, setCustomers] = useState([]);
  const [maritalStatuses, setMaritalStatuses] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState({
    name: "",
    cpf: "",
    rg: "",
    address_street: "",
    address_number: "",
    address_compl: "",
    address_city: "",
    address_state: "",
    address_neighborhood: "",
    address_zipcode: "",
    phone: "",
    mail: "",
    nationality: "",
    marital_status: null,
    profession: null,
    documents_last_updated_at: getMaxTodayDate(),
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [referenceErrors, setReferenceErrors] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [importedStatusFilter, setImportedStatusFilter] = useState("all");
  const [documentsUpdatedFrom, setDocumentsUpdatedFrom] = useState("");
  const [documentsUpdatedTo, setDocumentsUpdatedTo] = useState("");
  const [tabIndex, setTabIndex] = useState(0);
  const [references, setReferences] = useState([]);
  const [banks, setBanks] = useState([]);
  const [banksList, setBanksList] = useState([]);
  const [bankErrors, setBankErrors] = useState({});
  const [openFilesModal, setOpenFilesModal] = useState(false);
  const [customerForFiles, setCustomerForFiles] = useState(null);
  const [customerFilesList, setCustomerFilesList] = useState([]);
  const [newFileFiles, setNewFileFiles] = useState([]); // Array de { file: File, title: string }
  const [dragOver, setDragOver] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // Função para formatar o CPF
  const formatCPF = (cpf) => {
    if (!cpf) return "";
    const rawValue = cpf.replace(/\D/g, ""); // Remove caracteres não numéricos
    
    // Aplica a formatação progressiva
    if (rawValue.length <= 3) {
      return rawValue;
    } else if (rawValue.length <= 6) {
      return `${rawValue.slice(0, 3)}.${rawValue.slice(3)}`;
    } else if (rawValue.length <= 9) {
      return `${rawValue.slice(0, 3)}.${rawValue.slice(3, 6)}.${rawValue.slice(6)}`;
    } else {
      return `${rawValue.slice(0, 3)}.${rawValue.slice(3, 6)}.${rawValue.slice(6, 9)}-${rawValue.slice(9, 11)}`;
    }
  };

  // Função para formatar o número de telefone
  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    
    // Remove todos os caracteres não numéricos
    const rawValue = phone.replace(/\D/g, "");
    
    // Aplica a formatação brasileira
    if (rawValue.length >= 11) {
      // Formato: (11) 99999-9999
      return `(${rawValue.slice(0, 2)}) ${rawValue.slice(2, 7)}-${rawValue.slice(7, 11)}`;
    } else if (rawValue.length >= 7) {
      // Formato parcial: (11) 9999-9999
      return `(${rawValue.slice(0, 2)}) ${rawValue.slice(2, 6)}-${rawValue.slice(6)}`;
    } else if (rawValue.length >= 3) {
      // Formato parcial: (11) 9999
      return `(${rawValue.slice(0, 2)}) ${rawValue.slice(2)}`;
    } else if (rawValue.length >= 1) {
      // Formato parcial: (1
      return `(${rawValue}`;
    }
    
    return rawValue;
  };

  // Função para extrair apenas os dígitos do telefone
  const extractPhoneDigits = (phone) => {
    return phone ? phone.replace(/\D/g, "") : "";
  };

  // Função para formatar o CEP
  const formatZipCode = (zipcode) => {
    if (!zipcode) return "";
    
    // Remove todos os caracteres não numéricos
    const rawValue = zipcode.replace(/\D/g, "");
    
    // Aplica a formatação brasileira de CEP
    if (rawValue.length >= 6) {
      // Formato: 12345-678
      return `${rawValue.slice(0, 5)}-${rawValue.slice(5, 8)}`;
    }
    
    return rawValue;
  };

  // Função para extrair apenas os dígitos do CEP
  const extractZipCodeDigits = (zipcode) => {
    return zipcode ? zipcode.replace(/\D/g, "") : "";
  };

  // Função para buscar endereço pelo CEP
  const handleCepBlur = async (cep) => {
    const cleanCep = extractZipCodeDigits(cep);
    
    // Só busca se o CEP tiver 8 dígitos
    if (cleanCep.length === 8) {
      const addressData = await fetchAddress(cleanCep);
      
      if (addressData) {
        setCurrentCustomer(prevCustomer => ({
          ...prevCustomer,
          // Só preenche campos que estiverem em branco
          address_street: !prevCustomer.address_street && addressData.street ? addressData.street : prevCustomer.address_street,
          address_neighborhood: !prevCustomer.address_neighborhood && addressData.neighborhood ? addressData.neighborhood : prevCustomer.address_neighborhood,
          address_city: !prevCustomer.address_city && addressData.city ? addressData.city : prevCustomer.address_city,
          address_state: !prevCustomer.address_state && addressData.state ? addressData.state : prevCustomer.address_state,
          address_compl: !prevCustomer.address_compl && addressData.complement ? addressData.complement : prevCustomer.address_compl,
        }));
      }
    }
  };

  const fetchCustomers = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", String(page + 1));
    params.set("page_size", String(rowsPerPage));
    if (searchTerm) params.set("contains", searchTerm);
    if (importedStatusFilter !== "all") params.set("imported_status", importedStatusFilter);
    if (documentsUpdatedFrom) params.set("documents_last_updated_at_from", documentsUpdatedFrom);
    if (documentsUpdatedTo) params.set("documents_last_updated_at_to", documentsUpdatedTo);
    fetch(`/backend/customers/?${params.toString()}`, { credentials: "include" })
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = "Falha ao carregar os clientes";
          try {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.detail) {
              errorMessage = errorData.detail;
            }
          } catch (e) {
            console.warn("Não foi possível extrair mensagem de erro específica:", e);
          }
          throw new Error(errorMessage);
        }
        return response.json();
      })
      .then((data) => {
        setCustomers(data.results);
        setTotalRecords(data.count);
      })
      .catch((error) => {
        console.error("Erro ao carregar clientes:", error);
        alert(error.message);
      });
  }, [page, rowsPerPage, searchTerm, importedStatusFilter, documentsUpdatedFrom, documentsUpdatedTo]);

  const fetchMaritalStatuses = useCallback(() => {
    fetch("/backend/marital-status/", { credentials: "include" })
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = "Falha ao carregar os estados civis";
          try {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.detail) {
              errorMessage = errorData.detail;
            }
          } catch (e) {
            console.warn("Não foi possível extrair mensagem de erro específica:", e);
          }
          throw new Error(errorMessage);
        }
        return response.json();
      })
      .then((data) => {
        setMaritalStatuses(data.results);
      })
      .catch((error) => {
        console.error("Erro ao buscar estados civis:", error);
        alert(error.message);
      });
  }, []);

  const fetchProfessions = useCallback(() => {
    fetch("/backend/professions/", { credentials: "include" })
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = "Falha ao carregar as profissões";
          try {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.detail) {
              errorMessage = errorData.detail;
            }
          } catch (e) {
            console.warn("Não foi possível extrair mensagem de erro específica:", e);
          }
          throw new Error(errorMessage);
        }
        return response.json();
      })
      .then((data) => {
        setProfessions(data.results);
      })
      .catch((error) => {
        console.error("Erro ao buscar profissões:", error);
        alert(error.message);
      });
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers, page, rowsPerPage, searchTerm, importedStatusFilter, documentsUpdatedFrom, documentsUpdatedTo]);

  useEffect(() => {
    fetchMaritalStatuses();
  }, [fetchMaritalStatuses]);

  useEffect(() => {
    fetchProfessions();
  }, [fetchProfessions]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const fetchReferences = useCallback(async (customerId) => {
    try {
      const response = await fetch(`/backend/customer-references/?customer_id=${customerId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        const formattedReferences = data.results.map(ref => ({
          ...ref,
          phone: formatPhoneNumber(ref.phone || ""),
        }));
        setReferences(formattedReferences);
      } else {
        setReferences([]);
      }
    } catch (error) {
      console.error("Erro ao carregar referências:", error);
      setReferences([]);
    }
  }, []);

  const fetchBanks = useCallback(async (customerId) => {
    try {
      const response = await fetch(`/backend/customer-banks/?customer_id=${customerId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setBanks(data.results || []);
      } else {
        setBanks([]);
      }
    } catch (error) {
      console.error("Erro ao carregar bancos:", error);
      setBanks([]);
    }
  }, []);

  const fetchBanksList = useCallback(async () => {
    try {
      const response = await fetch(`/backend/banks/?page_size=1000`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setBanksList(data.results || []);
      } else {
        setBanksList([]);
      }
    } catch (error) {
      console.error("Erro ao carregar lista de bancos:", error);
      setBanksList([]);
    }
  }, []);

  useEffect(() => {
    fetchBanksList();
  }, [fetchBanksList]);

  const handleEdit = async (customer) => {
    setFieldErrors({});
    setReferenceErrors({});
    setBankErrors({});
    setIsEditing(true);
    setTabIndex(0);
    setLoadingCustomer(true);
    setOpenModal(true);
    try {
      const response = await fetch(`/backend/customers/${customer.id}/`);
      if (!response.ok) throw new Error("Falha ao carregar o cliente");
      const fullCustomer = await response.json();
      const customerWithFormattedFields = {
        ...fullCustomer,
        name: fullCustomer.name || "",
        cpf: fullCustomer.cpf || "",
        rg: fullCustomer.rg || "",
        nationality: fullCustomer.nationality || "",
        marital_status: fullCustomer.marital_status || null,
        profession: fullCustomer.profession || null,
        phone: formatPhoneNumber(fullCustomer.phone || ""),
        mail: fullCustomer.mail || "",
        address_street: fullCustomer.address_street || "",
        address_number: fullCustomer.address_number || "",
        address_compl: fullCustomer.address_compl || "",
        address_city: fullCustomer.address_city || "",
        address_state: fullCustomer.address_state || "",
        address_neighborhood: fullCustomer.address_neighborhood || "",
        address_zipcode: formatZipCode(fullCustomer.address_zipcode || ""),
        documents_last_updated_at: fullCustomer.documents_last_updated_at || "",
      };
      setCurrentCustomer(customerWithFormattedFields);
      fetchReferences(fullCustomer.id);
      fetchBanks(fullCustomer.id);
    } catch (error) {
      console.error("Erro ao carregar cliente:", error);
    } finally {
      setLoadingCustomer(false);
    }
  };

  const handleAdd = () => {
    setCurrentCustomer({
      name: "",
      cpf: "",
      rg: "",
      address_street: "",
      address_number: "",
      address_compl: "",
      address_city: "",
      address_state: "",
      address_neighborhood: "",
      address_zipcode: "",
      phone: "",
      mail: "",
      marital_status: null,
      profession: null,
      nationality: "",
      documents_last_updated_at: getMaxTodayDate(),
    });
    setFieldErrors({});
    setReferenceErrors({});
    setBankErrors({});
    setIsEditing(false);
    setTabIndex(0);
    setOpenModal(true);
    setReferences([]);
    setBanks([]);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleCloseWithConfirmation = () => {
    if (window.confirm("Você tem certeza que deseja fechar? Os dados não salvos serão perdidos.")) {
      setOpenModal(false);
    }
  };

  const handleSave = async () => {
    // Limpa erros anteriores
    setFieldErrors({});
    setReferenceErrors({});
    setBankErrors({});
    
    // Validação de campos obrigatórios no frontend
    const errors = {};
    if (!currentCustomer.name || !currentCustomer.name.trim()) {
      errors.name = "O nome é obrigatório";
    }

    if (currentCustomer.documents_last_updated_at) {
      if (currentCustomer.documents_last_updated_at < MIN_CONTRACT_DATE) {
        errors.documents_last_updated_at = "A data deve ser a partir de 01/01/2000.";
      } else if (currentCustomer.documents_last_updated_at > getMaxTodayDate()) {
        errors.documents_last_updated_at = "A data não pode ser futura.";
      }
    }
    
    // Validação de referências: se telefone ou vínculo foram informados, o nome é obrigatório
    const refErrors = {};
    let hasReferenceErrors = false;
    references.forEach((reference, index) => {
      const hasPhone = reference.phone && reference.phone.trim();
      const hasRelationship = reference.relationship && reference.relationship.trim();
      const hasName = reference.name && reference.name.trim();
      
      // Se algum campo opcional foi preenchido, o nome é obrigatório
      if ((hasPhone || hasRelationship) && !hasName) {
        refErrors[index] = "O nome é obrigatório quando telefone ou vínculo são informados.";
        hasReferenceErrors = true;
      }
    });
    
    if (hasReferenceErrors) {
      setReferenceErrors(refErrors);
      // Muda para a aba de referências se houver erros
      setTabIndex(3);
      return;
    }

    // Validação de bancos: o banco é obrigatório
    const bnkErrors = {};
    let hasBankErrors = false;
    banks.forEach((bank, index) => {
      // Verifica se o banco está informado (pode ser um objeto com id ou um número)
      const hasBank = bank.bank && (
        (typeof bank.bank === 'object' && bank.bank.id) || 
        (typeof bank.bank === 'number')
      );
      
      // O banco é obrigatório
      if (!hasBank) {
        bnkErrors[index] = "O banco é obrigatório.";
        hasBankErrors = true;
      }
    });
    
    if (hasBankErrors) {
      setBankErrors(bnkErrors);
      // Muda para a aba de bancos se houver erros
      setTabIndex(4);
      return;
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      if (errors.documents_last_updated_at) {
        setTabIndex(0);
      }
      return;
    }

    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `/backend/customers/${currentCustomer.id}/`
      : "/backend/customers/";

    // Prepara os dados para envio, removendo as máscaras do telefone e CEP
    const customerDataToSave = {
      ...currentCustomer,
      phone: extractPhoneDigits(currentCustomer.phone),
      address_zipcode: extractZipCodeDigits(currentCustomer.address_zipcode),
      // Converte o objeto marital_status para marital_status_id (seguindo padrão do backend)
      marital_status_id: currentCustomer.marital_status?.id || null,
      // Converte o objeto profession para profession_id (seguindo padrão do backend)
      profession_id: currentCustomer.profession?.id || null,
      documents_last_updated_at: currentCustomer.documents_last_updated_at || null,
    };
    
    // Remove os campos de objetos pois o backend espera apenas os IDs
    delete customerDataToSave.marital_status;
    delete customerDataToSave.profession;

    try {
      const response = await fetch(url, {
        method: method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(customerDataToSave),
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Aplica a formatação do telefone e CEP nos dados retornados
        const dataWithFormattedFields = {
          ...data,
          phone: formatPhoneNumber(data.phone || ""),
          address_zipcode: formatZipCode(data.address_zipcode || "")
        };
        
        // Salva as referências e bancos após salvar o cliente
        await saveReferences(data.id);
        await saveBanks(data.id);
        
        if (isEditing) {
          setCustomers((prevCustomers) =>
            prevCustomers.map((customer) =>
              customer.id === data.id ? data : customer
            )
          );
          // Atualiza o currentCustomer com os campos formatados
          setCurrentCustomer(dataWithFormattedFields);
        } else {
          setCustomers((prevCustomers) => [...prevCustomers, data]);
        }
        setFieldErrors({});
        setReferenceErrors({});
        setBankErrors({});
        let successMessage = "Cliente salvo com sucesso!";
        if (data.file_at_contracts_updated > 0) {
          const count = data.file_at_contracts_updated;
          successMessage += `\n\nDatas "ajuizar em" atualizadas: ${count} contrato${count === 1 ? "" : "s"}.`;
        }
        alert(successMessage);
        setOpenModal(false);
        fetchCustomers();
      } else {
        // Tenta extrair os erros específicos de cada campo do backend
        const errors = {};
        let generalError = null;
        
        try {
          const errorData = await response.json();
          
          // Mapeia os erros do backend para os campos do formulário
          const fieldMapping = {
            name: 'name',
            cpf: 'cpf',
            rg: 'rg',
            phone: 'phone',
            mail: 'mail',
            nationality: 'nationality',
            address_street: 'address_street',
            address_number: 'address_number',
            address_compl: 'address_compl',
            address_neighborhood: 'address_neighborhood',
            address_city: 'address_city',
            address_state: 'address_state',
            address_zipcode: 'address_zipcode',
            marital_status_id: 'marital_status',
            profession_id: 'profession',
            documents_last_updated_at: 'documents_last_updated_at',
          };
          
          // Processa erros de campos específicos
          Object.keys(fieldMapping).forEach(backendField => {
            if (errorData[backendField] && Array.isArray(errorData[backendField])) {
              errors[fieldMapping[backendField]] = errorData[backendField][0];
            }
          });
          
          // Erros gerais
          if (errorData.error) {
            generalError = errorData.error;
          } else if (errorData.non_field_errors && errorData.non_field_errors[0]) {
            generalError = errorData.non_field_errors[0];
          } else if (errorData.detail) {
            generalError = errorData.detail;
          }
          
          // Se houver erros de campos, atualiza o estado
          if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
          }
          
          // Monta mensagem de erro geral
          if (generalError) {
            alert(generalError);
          } else if (Object.keys(errors).length > 0) {
            // Se houver erros de campos, cria uma mensagem resumida
            const errorFields = Object.keys(errors);
            alert(`Erro de validação nos campos: ${errorFields.join(', ')}. Verifique os campos destacados.`);
          } else {
            alert("Erro ao salvar cliente. Verifique os dados informados.");
          }
        } catch (e) {
          console.error("Erro ao fazer parse da resposta:", e);
          alert(`Erro HTTP ${response.status}: ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error("Erro na requisição:", error);
      alert("Erro de conexão: " + error.message);
    }
  };

  const saveReferences = async (customerId) => {
    // Busca referências existentes no backend
    const existingRefsResponse = await fetch(`/backend/customer-references/?customer_id=${customerId}`, {
      credentials: "include",
    });
    let existingRefs = [];
    if (existingRefsResponse.ok) {
      const existingData = await existingRefsResponse.json();
      existingRefs = existingData.results || [];
    }

    // IDs das referências que devem ser mantidas
    const referencesToKeep = references.filter(ref => ref.id).map(ref => ref.id);
    
    // Deleta referências que foram removidas
    for (const existingRef of existingRefs) {
      if (!referencesToKeep.includes(existingRef.id)) {
        try {
          await fetch(`/backend/customer-references/${existingRef.id}/`, {
            method: "DELETE",
            credentials: "include",
          });
        } catch (error) {
          console.error("Erro ao deletar referência:", error);
        }
      }
    }

    // Cria ou atualiza referências (apenas as que têm nome)
    for (const reference of references) {
      // Verifica se algum campo opcional foi preenchido
      const hasPhone = reference.phone && reference.phone.trim();
      const hasRelationship = reference.relationship && reference.relationship.trim();
      const hasName = reference.name && reference.name.trim();
      
      // Se algum campo opcional foi preenchido, o nome é obrigatório
      if ((hasPhone || hasRelationship) && !hasName) {
        console.warn("Referência ignorada: telefone ou vínculo informados sem nome");
        continue; // Pula referências inválidas
      }
      
      // Valida se a referência tem nome (obrigatório)
      if (!hasName) {
        continue; // Pula referências sem nome
      }

      const referenceData = {
        customer: customerId,
        name: reference.name.trim(),
        relationship: reference.relationship?.trim() || "",
        phone: extractPhoneDigits(reference.phone || ""),
      };

      try {
        if (reference.id) {
          // Atualiza referência existente
          const response = await fetch(`/backend/customer-references/${reference.id}/`, {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(referenceData),
          });
          if (!response.ok) {
            const errorData = await response.json();
            console.error("Erro ao atualizar referência:", errorData);
          }
        } else {
          // Cria nova referência
          const response = await fetch("/backend/customer-references/", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(referenceData),
          });
          if (!response.ok) {
            const errorData = await response.json();
            console.error("Erro ao criar referência:", errorData);
          }
        }
      } catch (error) {
        console.error("Erro ao salvar referência:", error);
      }
    }
  };

  const saveBanks = async (customerId) => {
    // Busca bancos existentes no backend
    const existingBanksResponse = await fetch(`/backend/customer-banks/?customer_id=${customerId}`, {
      credentials: "include",
    });
    let existingBanks = [];
    if (existingBanksResponse.ok) {
      const existingData = await existingBanksResponse.json();
      existingBanks = existingData.results || [];
    }

    // IDs dos bancos que devem ser mantidos
    const banksToKeep = banks.filter(bank => bank.id).map(bank => bank.id);
    
    // Deleta bancos que foram removidos
    for (const existingBank of existingBanks) {
      if (!banksToKeep.includes(existingBank.id)) {
        try {
          await fetch(`/backend/customer-banks/${existingBank.id}/`, {
            method: "DELETE",
            credentials: "include",
          });
        } catch (error) {
          console.error("Erro ao deletar banco:", error);
        }
      }
    }

    // Cria ou atualiza bancos (apenas os que têm banco selecionado)
    for (const bank of banks) {
      // Valida se o banco tem banco selecionado (obrigatório)
      // Pode ser um objeto com id ou um número diretamente
      const bankId = bank.bank && (
        (typeof bank.bank === 'object' && bank.bank.id) || 
        (typeof bank.bank === 'number' && bank.bank)
      );
      
      if (!bankId) {
        continue; // Pula bancos sem banco selecionado
      }

      const bankData = {
        customer: customerId,
        bank: bankId,
      };

      try {
        if (bank.id) {
          // Atualiza banco existente
          const response = await fetch(`/backend/customer-banks/${bank.id}/`, {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(bankData),
          });
          if (!response.ok) {
            const errorData = await response.json();
            console.error("Erro ao atualizar banco:", errorData);
          }
        } else {
          // Cria novo banco
          const response = await fetch("/backend/customer-banks/", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(bankData),
          });
          if (!response.ok) {
            const errorData = await response.json();
            console.error("Erro ao criar banco:", errorData);
          }
        }
      } catch (error) {
        console.error("Erro ao salvar banco:", error);
      }
    }
  };

  const handleDownloadTemplate = () => {
    window.open("/backend/customers/csv-template/", "_blank");
  };

  const handleOpenImportModal = () => {
    setOpenImportModal(true);
    setSelectedFile(null);
    setImportResult(null);
  };

  const handleCloseImportModal = () => {
    setOpenImportModal(false);
    setSelectedFile(null);
    setImportResult(null);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.name.endsWith(".csv")) {
        setSelectedFile(file);
        setImportResult(null);
      } else {
        alert("Por favor, selecione um arquivo CSV válido. O arquivo deve ter a extensão .csv");
        event.target.value = "";
      }
    }
  };

  const handleImportCSV = async () => {
    if (!selectedFile) {
      alert("Por favor, selecione um arquivo CSV antes de importar.");
      return;
    }

    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append("csv_file", selectedFile);

    try {
      const response = await fetch("/backend/customers/import-csv/", {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const contentType = response.headers.get("content-type") || "";
      let result;
      try {
        result = contentType.includes("application/json")
          ? await response.json()
          : { error: `Servidor retornou ${response.status}. Verifique se está logado.` };
      } catch {
        result = {
          error:
            response.status === 401 || response.status === 403
              ? "Sessão expirada. Faça login novamente."
              : response.status === 504
                ? "Tempo limite excedido. Parte dos registros pode ter sido importada. Verifique a lista e tente importar em lotes menores se necessário."
                : "Resposta inválida do servidor. Tente novamente.",
        };
      }

      if (response.ok || response.status === 207) {
        setImportResult(result);
        fetchCustomers();
      } else {
        throw new Error(
          result.error ||
            "Não foi possível importar o arquivo CSV. Verifique o formato do arquivo e tente novamente."
        );
      }
    } catch (error) {
      console.error("Erro ao importar CSV:", error);
      setImportResult({
        success: false,
        error: error.message,
      });
    } finally {
      setImporting(false);
    }
  };

  const handleAddReference = () => {
    setReferences([
      ...references,
      {
        id: null,
        name: "",
        relationship: "",
        phone: "",
      },
    ]);
  };

  const handleRemoveReference = (index) => {
    setReferences(references.filter((_, i) => i !== index));
    // Limpa todos os erros das referências, pois os índices mudam após a remoção
    setReferenceErrors({});
  };

  const handleReferenceChange = (index, field, value) => {
    const updatedReferences = [...references];
    if (field === "phone") {
      const rawValue = value.replace(/\D/g, "").slice(0, 11);
      updatedReferences[index] = {
        ...updatedReferences[index],
        phone: formatPhoneNumber(rawValue),
      };
    } else {
      updatedReferences[index] = {
        ...updatedReferences[index],
        [field]: value,
      };
    }
    setReferences(updatedReferences);
    
    // Limpa o erro da referência quando:
    // 1. O usuário preenche o nome
    // 2. O usuário remove o telefone ou vínculo que causou o erro
    if (referenceErrors[index]) {
      const updatedRef = updatedReferences[index];
      const hasPhone = updatedRef.phone && updatedRef.phone.trim();
      const hasRelationship = updatedRef.relationship && updatedRef.relationship.trim();
      const hasName = updatedRef.name && updatedRef.name.trim();
      
      // Se o nome foi preenchido OU se telefone e vínculo foram removidos, limpa o erro
      if (hasName || (!hasPhone && !hasRelationship)) {
        setReferenceErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
      }
    }
  };

  const handleAddBank = () => {
    setBanks([
      ...banks,
      {
        id: null,
        bank: null,
      },
    ]);
  };

  const handleRemoveBank = (index) => {
    setBanks(banks.filter((_, i) => i !== index));
    // Limpa todos os erros dos bancos, pois os índices mudam após a remoção
    setBankErrors({});
  };

  const handleBankChange = (index, field, value) => {
    const updatedBanks = [...banks];
    updatedBanks[index] = {
      ...updatedBanks[index],
      [field]: value,
    };
    setBanks(updatedBanks);
    
    // Limpa o erro do banco quando o usuário seleciona um banco
    if (bankErrors[index]) {
      const updatedBank = updatedBanks[index];
      // Verifica se o banco está informado (pode ser um objeto com id ou um número)
      const hasBank = updatedBank.bank && (
        (typeof updatedBank.bank === 'object' && updatedBank.bank.id) || 
        (typeof updatedBank.bank === 'number')
      );
      
      // Se o banco foi selecionado, limpa o erro
      if (hasBank) {
        setBankErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
      }
    }
  };

  const fetchCustomerFiles = useCallback(async (customerId) => {
    setFilesLoading(true);
    try {
      const response = await fetch(`/backend/customer-files/?customer_id=${customerId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCustomerFilesList(data.results || []);
      } else {
        setCustomerFilesList([]);
        try {
          const err = await response.json();
          const msg = err.detail || err.error || `Erro ${response.status} ao carregar arquivos.`;
          alert(msg);
        } catch (_) {
          alert(`Erro ${response.status} ao carregar arquivos.`);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar arquivos:", error);
      setCustomerFilesList([]);
      alert("Erro ao carregar arquivos. Verifique o console.");
    } finally {
      setFilesLoading(false);
    }
  }, []);

  const getTitleFromFilename = (filename) => {
    const name = filename.replace(/\.[^/.]+$/, "").trim() || filename;
    return name.slice(0, 100);
  };

  const processFiles = (files) => {
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/gif", "image/webp", "image/bmp"];
    const items = [];
    for (const file of Array.from(files || [])) {
      if (!allowedTypes.includes(file.type)) continue;
      const title = getTitleFromFilename(file.name);
      items.push({ file, title });
    }
    return items;
  };

  const handleAddFiles = (files) => {
    const items = processFiles(files);
    if (items.length === 0 && files?.length > 0) {
      alert("Apenas imagens (JPEG, PNG, GIF, WebP, BMP) e PDF são permitidos.");
      return;
    }
    setNewFileFiles((prev) => [...prev, ...items]);
  };

  const handleRemoveFileToUpload = (index) => {
    setNewFileFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateFileTitle = (index, title) => {
    setNewFileFiles((prev) => prev.map((item, i) => (i === index ? { ...item, title: title.slice(0, 100) } : item)));
  };

  const handleOpenFiles = (customer) => {
    setCustomerForFiles(customer);
    setOpenFilesModal(true);
    setNewFileFiles([]);
    setCustomerFilesList([]);
    if (customer?.id) {
      fetchCustomerFiles(customer.id);
    }
  };

  const handleCloseFilesModal = () => {
    setOpenFilesModal(false);
    setCustomerForFiles(null);
    setCustomerFilesList([]);
    setNewFileFiles([]);
  };

  const handleUploadFile = async () => {
    if (!customerForFiles?.id) return;
    if (newFileFiles.length === 0) {
      alert("Selecione ou arraste um ou mais arquivos (imagem ou PDF).");
      return;
    }
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/gif", "image/webp", "image/bmp"];
    const invalid = newFileFiles.find(({ file }) => !allowedTypes.includes(file.type));
    if (invalid) {
      alert("Apenas imagens (JPEG, PNG, GIF, WebP, BMP) e PDF são permitidos.");
      return;
    }
    const withEmptyTitle = newFileFiles.find(({ title }) => !(title || "").trim());
    if (withEmptyTitle) {
      alert("Informe o título para todos os arquivos.");
      return;
    }
    setUploadLoading(true);
    const toUpload = newFileFiles.map(({ file, title }) => ({ file, title: (title || "").trim().slice(0, 100) }));
    let successCount = 0;
    let lastError = null;
    try {
      for (const { file, title } of toUpload) {
        const formData = new FormData();
        formData.append("customer", customerForFiles.id);
        formData.append("title", title);
        formData.append("file", file);
        const response = await fetch("/backend/customer-files/", {
          method: "POST",
          credentials: "include",
          body: formData,
        });
        if (response.ok) {
          const data = await response.json();
          setCustomerFilesList((prev) => [data, ...prev]);
          successCount++;
        } else {
          try {
            const err = await response.json();
            lastError = err.file?.[0] || err.title?.[0] || err.detail || err.error || "Erro ao enviar arquivo.";
          } catch (_) {
            lastError = `Erro ${response.status}: ${response.statusText}`;
          }
          console.error("POST customer-files erro:", response.status, lastError);
        }
      }
      if (successCount > 0) {
        setNewFileFiles([]);
        if (document.getElementById("customer-file-input")) {
          document.getElementById("customer-file-input").value = "";
        }
      }
      if (lastError && successCount === 0) alert(lastError);
      else if (lastError && successCount < toUpload.length) {
        alert(`${successCount} de ${toUpload.length} arquivo(s) enviado(s). Erro no restante: ${lastError}`);
      }
    } catch (error) {
      console.error("Erro ao enviar arquivo:", error);
      alert("Erro ao enviar arquivo.");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Excluir este arquivo?")) return;
    try {
      const response = await fetch(`/backend/customer-files/${fileId}/`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        setCustomerFilesList((prev) => prev.filter((f) => f.id !== fileId));
      } else {
        alert("Erro ao excluir arquivo.");
      }
    } catch (error) {
      console.error("Erro ao excluir arquivo:", error);
      alert("Erro ao excluir arquivo.");
    }
  };

  const handleDownloadFile = async (fileId) => {
    try {
      const response = await fetch(`/backend/customer-files/${fileId}/download/`, {
        credentials: "include",
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        alert(err.detail || err.error || "Erro ao baixar arquivo.");
        return;
      }
      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "arquivo";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1].trim();
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error("Erro ao baixar arquivo:", err);
      alert("Erro ao baixar arquivo.");
    }
  };

  const handlePrintHonorarios = async (customerId, customerName) => {
    try {
      const response = await fetch(`/backend/customers/${customerId}/generate-honorarios-docx/`, {
        method: "GET",
      });

      if (!response.ok) {
        let errorMessage = "";
        const missingFields = [];
        
        try {
          const errorData = await response.json();
          
          // Verifica se há campos obrigatórios faltantes
          if (errorData.missing_fields && Array.isArray(errorData.missing_fields)) {
            missingFields.push(...errorData.missing_fields);
          }
          
          // Processa diferentes tipos de erro do backend
          if (missingFields.length > 0) {
            // Monta mensagem destacando os campos obrigatórios faltantes
            const messageParts = [];
            messageParts.push("⚠️ CAMPOS OBRIGATÓRIOS NÃO PREENCHIDOS:");
            messageParts.push("");
            messageParts.push("Os seguintes campos precisam ser preenchidos antes de gerar o documento:");
            messageParts.push("");
            missingFields.forEach((field, idx) => {
              messageParts.push(`${idx + 1}. ${field}`);
            });
            
            if (errorData.details) {
              messageParts.push("");
              messageParts.push(`Detalhes: ${errorData.details}`);
            }
            
            errorMessage = messageParts.join("\n");
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.error) {
            errorMessage = errorData.error;
            if (errorData.details) {
              errorMessage += `\n\nDetalhes: ${errorData.details}`;
            }
          }
        } catch (parseError) {
          // Se não conseguir parsear o JSON, usa mensagem baseada no status
          if (response.status === 404) {
            errorMessage = "Template de honorários não encontrado. Verifique se o arquivo 'honorarios_modelo.docx' existe.";
          } else if (response.status === 500) {
            errorMessage = "Erro interno do servidor ao gerar o documento. Tente novamente mais tarde ou entre em contato com o suporte.";
          } else if (response.status === 403) {
            errorMessage = "Você não tem permissão para gerar o documento de honorários deste cliente.";
          } else {
            errorMessage = `Erro ao gerar documento de honorários (Status: ${response.status}). Tente novamente.`;
          }
        }
        
        throw new Error(errorMessage);
      }

      // Verifica se a resposta é realmente um arquivo DOCX
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/vnd.openxmlformats-officedocument.wordprocessingml.document")) {
        const text = await response.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.detail || errorData.error || errorData.message || "O servidor retornou uma resposta inválida.");
        } catch {
          throw new Error("O servidor retornou uma resposta inesperada ao tentar gerar o documento.");
        }
      }

      // Cria um blob com o DOCX retornado
      const blob = await response.blob();
      
      // Verifica se o blob não está vazio
      if (blob.size === 0) {
        throw new Error("O documento gerado está vazio. Verifique se o cliente possui todos os dados necessários.");
      }
      
      // Cria uma URL temporária para o blob
      const url = window.URL.createObjectURL(blob);
      
      // Cria nome do arquivo seguro
      const safeName = customerName ? customerName.replace(/[^a-zA-Z0-9 _-]/g, '').replace(/\s+/g, '_').substring(0, 50) : customerId;
      
      // Cria um link temporário e clica nele para iniciar o download
      const link = document.createElement("a");
      link.href = url;
      link.download = `honorarios_${safeName}.docx`;
      document.body.appendChild(link);
      link.click();
      
      // Remove o link e libera a URL
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error("Erro ao gerar documento de honorários:", error);
      alert(`Erro ao gerar documento de honorários:\n\n${error.message || "Erro desconhecido. Tente novamente ou entre em contato com o suporte."}`);
    }
  };

  const handleDelete = (customerId) => {
    if (window.confirm("Você tem certeza que deseja excluir este cliente?")) {
      fetch(`/backend/customers/${customerId}/`, {
        method: "DELETE",
        credentials: "include",
      })
        .then(async (response) => {
          if (response.ok) {
            setCustomers((prevCustomers) =>
              prevCustomers.filter((customer) => customer.id !== customerId)
            );
            alert("Cliente excluído com sucesso!");
            fetchCustomers();
          } else {
            // Tenta extrair a mensagem de erro específica do backend
            let errorMessage = "Erro ao excluir cliente";
            try {
              const errorData = await response.json();
              if (errorData.error) {
                errorMessage = errorData.error;
              } else if (errorData.detail) {
                errorMessage = errorData.detail;
              }
            } catch (e) {
              // Se não conseguir parsear o JSON, usa a mensagem padrão
              console.warn("Não foi possível extrair mensagem de erro específica:", e);
            }
            throw new Error(errorMessage);
          }
        })
        .catch((error) => {
          console.error("Erro ao excluir cliente:", error);
          alert(error.message);
        });
    }
  };

  return (
    <div className="w-full overflow-auto">
      <MainCard
        title="Cadastro de clientes"
        actions={[
          <Tooltip key="template-csv" title="Baixar template CSV">
            <AddButton onClick={handleDownloadTemplate}>
              <IconDownload stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
              Template CSV
            </AddButton>
          </Tooltip>,
          ...(canImportData ? [
          <Tooltip key="import-csv" title="Importar clientes via CSV">
            <AddButton onClick={handleOpenImportModal}>
              <IconUpload stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
              Importar CSV
            </AddButton>
          </Tooltip>,
          ] : []),
          <AddButton key="add-customer" onClick={handleAdd}>
            <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
            Adicionar cliente
          </AddButton>,
        ]}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <SearchSection onSearchChange={(value) => { setSearchTerm(value); setPage(0); }} />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="imported-status-filter-label">Status importado</InputLabel>
            <Select
              labelId="imported-status-filter-label"
              id="imported-status-filter"
              value={importedStatusFilter}
              label="Status importado"
              onChange={(e) => {
                setImportedStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="imported">Somente importados</MenuItem>
              <MenuItem value="not_imported">Somente não importados</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "nowrap" }}>
            <TextField
              label="Atual. documentos de"
              type="date"
              size="small"
              value={documentsUpdatedFrom}
              onChange={(e) => {
                setDocumentsUpdatedFrom(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 190 }}
            />
            <TextField
              label="Atual. documentos até"
              type="date"
              size="small"
              value={documentsUpdatedTo}
              onChange={(e) => {
                setDocumentsUpdatedTo(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 190 }}
            />
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
          <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }}>ID</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Tooltip title="Data da última atualização de documentos">
                  <span>Atualização de documentos</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((customer) => (
              <TableRow key={customer.id}>
                <TableCell sx={{ color: "text.secondary", fontFamily: "monospace" }}>{customer.id}</TableCell>
                <TableCell>
                  <CustomerNameButton customerId={customer.id}>{customer.name}</CustomerNameButton>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {formatDateBR(customer.documents_last_updated_at)}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Arquivos (imagens e PDF)">
                    <FilesButton onClick={() => handleOpenFiles(customer)}>
                      <IconPhoto stroke={1.5} size="1.3rem" />
                    </FilesButton>
                  </Tooltip>
                  <Tooltip title="Gerar Honorários">
                    <PrintButton onClick={() => handlePrintHonorarios(customer.id, customer.name)}>
                      <IconFileText stroke={1.5} size="1.3rem" />
                    </PrintButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <EditButton onClick={() => handleEdit(customer)}>
                      <IconEdit stroke={1.5} size="1.3rem" />
                    </EditButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <DeleteButton onClick={() => handleDelete(customer.id)}>
                      <IconTrash stroke={1.5} size="1.3rem" />
                    </DeleteButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MainCard>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100, 150]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Clientes por página"
      />

      <Dialog 
        open={openModal} 
        onClose={(event, reason) => {
          // Impede o fechamento do modal ao clicar fora
          if (reason === 'backdropClick') {
            return;
          }
          // Para tecla ESC, solicita confirmação
          if (reason === 'escapeKeyDown') {
            handleCloseWithConfirmation();
            return;
          }
          handleCloseModal();
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <span>{isEditing ? "Editar Cliente" : "Adicionar Cliente"}</span>
            {isEditing && currentCustomer.id && (
              <Typography variant="body2" sx={{ color: "text.secondary", fontFamily: "monospace", fontSize: "0.85rem" }}>
                ID: {currentCustomer.id}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        {loadingCustomer && <LinearProgress />}
        <DialogContent>
          {/* Campos principais - sempre visíveis no topo */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* Nome */}
            <Grid item xs={12}>
              <TextField
                label="Nome"
                value={currentCustomer.name || ""}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 100);
                  setCurrentCustomer({
                    ...currentCustomer,
                    name: value,
                  });
                  if (fieldErrors.name) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.name;
                      return newErrors;
                    });
                  }
                }}
                fullWidth
                required
                inputProps={{ maxLength: 100 }}
                autoComplete="new-password"
                error={!!fieldErrors.name}
                helperText={fieldErrors.name || "Máximo 100 caracteres"}
              />
            </Grid>

            {/* CPF */}
            <Grid item xs={12}>
              <TextField
                label="CPF"
                value={formatCPF(currentCustomer.cpf || "")}
                onChange={(e) => {
                  const rawValue = e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 11);
                  setCurrentCustomer({ ...currentCustomer, cpf: rawValue });
                  if (fieldErrors.cpf) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.cpf;
                      return newErrors;
                    });
                  }
                }}
                fullWidth
                inputProps={{ maxLength: 14 }} // 14 para incluir formatação: 000.000.000-00
                autoComplete="new-password"
                error={!!fieldErrors.cpf}
                helperText={fieldErrors.cpf || "11 dígitos"}
              />
            </Grid>
          </Grid>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
            <Tabs value={tabIndex} onChange={(e, newValue) => setTabIndex(newValue)}>
              <Tab label="Dados Pessoais" />
              <Tab label="Endereço" />
              <Tab label="Contato" />
              <Tab label="Referências" />
              <Tab label="Bancos" />
            </Tabs>
          </Box>

          {/* Aba: Dados Pessoais */}
          <TabPanel value={tabIndex} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="RG"
                  value={currentCustomer.rg || ""}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 20);
                    setCurrentCustomer({
                      ...currentCustomer,
                      rg: value,
                    });
                    if (fieldErrors.rg) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.rg;
                        return newErrors;
                      });
                    }
                  }}
                  fullWidth
                  inputProps={{ maxLength: 20 }}
                  autoComplete="new-password"
                  error={!!fieldErrors.rg}
                  helperText={fieldErrors.rg || "Máximo 20 caracteres"}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Nacionalidade"
                  value={currentCustomer.nationality || ""}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 30);
                    setCurrentCustomer({
                      ...currentCustomer,
                      nationality: value,
                    });
                    if (fieldErrors.nationality) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.nationality;
                        return newErrors;
                      });
                    }
                  }}
                  fullWidth
                  inputProps={{ maxLength: 30 }}
                  autoComplete="new-password"
                  error={!!fieldErrors.nationality}
                  helperText={fieldErrors.nationality || "Máximo 30 caracteres"}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={maritalStatuses}
                  getOptionLabel={(option) => option.name}
                  value={
                    maritalStatuses.find(
                      (m) => m.id === currentCustomer.marital_status?.id
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setCurrentCustomer({
                      ...currentCustomer,
                      marital_status: newValue || null,
                    });
                    if (fieldErrors.marital_status) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.marital_status;
                        return newErrors;
                      });
                    }
                  }}
                  disablePortal
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Estado civil"
                      fullWidth
                      autoComplete="off"
                      error={!!fieldErrors.marital_status}
                      helperText={fieldErrors.marital_status}
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'new-password',
                        'data-form-type': 'other',
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={professions}
                  getOptionLabel={(option) => option.name}
                  value={
                    professions.find(
                      (p) => p.id === currentCustomer.profession?.id
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setCurrentCustomer({
                      ...currentCustomer,
                      profession: newValue || null,
                    });
                    if (fieldErrors.profession) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.profession;
                        return newErrors;
                      });
                    }
                  }}
                  disablePortal
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Profissão"
                      fullWidth
                      autoComplete="off"
                      error={!!fieldErrors.profession}
                      helperText={fieldErrors.profession}
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'new-password',
                        'data-form-type': 'other',
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Data da última atualização de documentos"
                  type="date"
                  value={currentCustomer.documents_last_updated_at || ""}
                  onChange={(e) => {
                    setCurrentCustomer({
                      ...currentCustomer,
                      documents_last_updated_at: e.target.value || "",
                    });
                    if (fieldErrors.documents_last_updated_at) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.documents_last_updated_at;
                        return newErrors;
                      });
                    }
                  }}
                  fullWidth
                  autoComplete="new-password"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: MIN_CONTRACT_DATE, max: getMaxTodayDate() }}
                  error={!!fieldErrors.documents_last_updated_at}
                  helperText={
                    fieldErrors.documents_last_updated_at || "Entre 01/01/2000 e hoje"
                  }
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Aba: Endereço */}
          <TabPanel value={tabIndex} index={1}>
            <Grid container spacing={2}>
              {/* CEP - Primeiro campo para buscar endereço */}
              <Grid item xs={4}>
                <TextField
                  label="CEP"
                  value={currentCustomer.address_zipcode || ""}
                  onChange={(e) => {
                    // Remove todos os caracteres não numéricos e limita a 8 dígitos
                    const rawValue = e.target.value.replace(/\D/g, "").slice(0, 8);
                    
                    // Aplica a formatação usando a função formatZipCode
                    const formattedValue = formatZipCode(rawValue);
                    
                    setCurrentCustomer({
                      ...currentCustomer,
                      address_zipcode: formattedValue,
                    });
                    if (fieldErrors.address_zipcode) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.address_zipcode;
                        return newErrors;
                      });
                    }
                  }}
                  onBlur={(e) => handleCepBlur(e.target.value)}
                  fullWidth
                  variant="outlined"
                  inputProps={{ maxLength: 9 }} // 12345-678
                  placeholder="12345-678"
                  autoComplete="new-password"
                  disabled={cepLoading}
                  error={!!fieldErrors.address_zipcode || !!cepError}
                  helperText={
                    fieldErrors.address_zipcode
                      ? fieldErrors.address_zipcode
                      : cepLoading 
                        ? "Buscando endereço..." 
                        : cepError 
                          ? `Erro: ${cepError}` 
                          : "Digite o CEP para buscar o endereço automaticamente"
                  }
                />
              </Grid>

              {/* Rua */}
              <Grid item xs={6}>
                <TextField
                  label="Rua"
                  value={currentCustomer.address_street || ""}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 100);
                    setCurrentCustomer({
                      ...currentCustomer,
                      address_street: value,
                    });
                    if (fieldErrors.address_street) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.address_street;
                        return newErrors;
                      });
                    }
                  }}
                  fullWidth
                  variant="outlined"
                  inputProps={{ maxLength: 100 }}
                  autoComplete="new-password"
                  error={!!fieldErrors.address_street}
                  helperText={fieldErrors.address_street || "Máximo 100 caracteres"}
                />
              </Grid>

              {/* Número */}
              <Grid item xs={2}>
                <TextField
                  label="Nº"
                  value={currentCustomer.address_number || ""}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 20);
                    setCurrentCustomer({
                      ...currentCustomer,
                      address_number: value,
                    });
                    if (fieldErrors.address_number) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.address_number;
                        return newErrors;
                      });
                    }
                  }}
                  fullWidth
                  variant="outlined"
                  inputProps={{ maxLength: 20 }}
                  autoComplete="new-password"
                  error={!!fieldErrors.address_number}
                  helperText={fieldErrors.address_number || "Máximo 20 caracteres"}
                />
              </Grid>

              {/* Bairro */}
              <Grid item xs={6}>
                <TextField
                  label="Bairro"
                  value={currentCustomer.address_neighborhood || ""}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 60);
                    setCurrentCustomer({
                      ...currentCustomer,
                      address_neighborhood: value,
                    });
                    if (fieldErrors.address_neighborhood) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.address_neighborhood;
                        return newErrors;
                      });
                    }
                  }}
                  fullWidth
                  variant="outlined"
                  inputProps={{ maxLength: 60 }}
                  autoComplete="new-password"
                  error={!!fieldErrors.address_neighborhood}
                  helperText={fieldErrors.address_neighborhood || "Máximo 60 caracteres"}
                />
              </Grid>

              {/* Complemento */}
              <Grid item xs={6}>
                <TextField
                  label="Complemento"
                  value={currentCustomer.address_compl || ""}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 20);
                    setCurrentCustomer({
                      ...currentCustomer,
                      address_compl: value,
                    });
                    if (fieldErrors.address_compl) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.address_compl;
                        return newErrors;
                      });
                    }
                  }}
                  fullWidth
                  variant="outlined"
                  inputProps={{ maxLength: 20 }}
                  autoComplete="new-password"
                  error={!!fieldErrors.address_compl}
                  helperText={
                    fieldErrors.address_compl 
                      ? fieldErrors.address_compl 
                      : `${(currentCustomer.address_compl || "").length}/20 caracteres`
                  }
                />
              </Grid>

              {/* Cidade */}
              <Grid item xs={4}>
                <TextField
                  label="Cidade"
                  value={currentCustomer.address_city || ""}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 60);
                    setCurrentCustomer({
                      ...currentCustomer,
                      address_city: value,
                    });
                    if (fieldErrors.address_city) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.address_city;
                        return newErrors;
                      });
                    }
                  }}
                  fullWidth
                  variant="outlined"
                  inputProps={{ maxLength: 60 }}
                  autoComplete="new-password"
                  error={!!fieldErrors.address_city}
                  helperText={fieldErrors.address_city || "Máximo 60 caracteres"}
                />
              </Grid>

              {/* UF */}
              <Grid item xs={2}>
                <TextField
                  label="UF"
                  value={currentCustomer.address_state || ""}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().slice(0, 2);
                    setCurrentCustomer({
                      ...currentCustomer,
                      address_state: value,
                    });
                    if (fieldErrors.address_state) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.address_state;
                        return newErrors;
                      });
                    }
                  }}
                  fullWidth
                  variant="outlined"
                  inputProps={{ maxLength: 2 }}
                  autoComplete="new-password"
                  error={!!fieldErrors.address_state}
                  helperText={fieldErrors.address_state || "Ex: SP, RJ, MG"}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Aba: Contato */}
          <TabPanel value={tabIndex} index={2}>
            <Grid container spacing={2}>
              {/* E-mail */}
              <Grid item xs={12}>
                <TextField
                  label="E-mail"
                  value={currentCustomer.mail || ""}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 60);
                    setCurrentCustomer({
                      ...currentCustomer,
                      mail: value,
                    });
                    if (fieldErrors.mail) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.mail;
                        return newErrors;
                      });
                    }
                  }}
                  fullWidth
                  inputProps={{ maxLength: 60 }}
                  autoComplete="new-password"
                  error={!!fieldErrors.mail}
                  helperText={fieldErrors.mail || "Máximo 60 caracteres"}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Telefone"
                  value={currentCustomer.phone || ""}
                  onChange={(e) => {
                    // Remove todos os caracteres não numéricos e limita a 11 dígitos
                    const rawValue = e.target.value.replace(/\D/g, "").slice(0, 11);
                    
                    // Aplica a formatação usando a função formatPhoneNumber
                    const formattedValue = formatPhoneNumber(rawValue);
                    
                    setCurrentCustomer({
                      ...currentCustomer,
                      phone: formattedValue,
                    });
                    if (fieldErrors.phone) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.phone;
                        return newErrors;
                      });
                    }
                  }}
                  fullWidth
                  inputProps={{ maxLength: 15 }} // (11) 99999-9999
                  placeholder="(11) 99999-9999"
                  autoComplete="new-password"
                  error={!!fieldErrors.phone}
                  helperText={fieldErrors.phone || "10 ou 11 dígitos"}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Aba: Referências */}
          <TabPanel value={tabIndex} index={3}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ typography: 'subtitle1', fontWeight: 500 }}>Referências Pessoais</Box>
                  <AddButton onClick={handleAddReference} sx={{ width: 200 }}>
                    <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
                    Adicionar Referência
                  </AddButton>
                </Box>
              </Grid>

              {references.length === 0 ? (
                <Grid item xs={12}>
                  <Box 
                    sx={{ 
                      textAlign: 'center', 
                      py: 3, 
                      px: 3,
                      color: 'text.secondary',
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'action.hover',
                    }}
                  >
                    Nenhuma referência cadastrada. Clique em "Adicionar Referência" para adicionar.
                  </Box>
                </Grid>
              ) : (
                references.map((reference, index) => (
                  <Grid item xs={12} key={index}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        p: 1.5,
                        position: 'relative',
                        backgroundColor: 'background.paper',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        '&:hover': {
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <Button
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          minWidth: 'auto',
                          width: 28,
                          height: 28,
                          p: 0,
                          zIndex: 10,
                          color: 'error.main',
                          backgroundColor: 'transparent',
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'error.dark',
                          },
                        }}
                        onClick={() => handleRemoveReference(index)}
                        size="small"
                        title="Remover referência"
                      >
                        <IconX size="0.9rem" />
                      </Button>

                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            label="Nome"
                            value={reference.name || ""}
                            onChange={(e) => {
                              const value = e.target.value.slice(0, 100);
                              handleReferenceChange(index, "name", value);
                            }}
                            fullWidth
                            required
                            error={!!referenceErrors[index]}
                            helperText={referenceErrors[index]}
                            inputProps={{ maxLength: 100 }}
                            autoComplete="new-password"
                            variant="outlined"
                            size="small"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            label="Vínculo"
                            value={reference.relationship || ""}
                            onChange={(e) => {
                              const value = e.target.value.slice(0, 50);
                              handleReferenceChange(index, "relationship", value);
                            }}
                            fullWidth
                            inputProps={{ maxLength: 50 }}
                            autoComplete="new-password"
                            placeholder="Ex: Pai, Mãe, Amigo, etc."
                            variant="outlined"
                            size="small"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            label="Telefone"
                            value={reference.phone || ""}
                            onChange={(e) => {
                              handleReferenceChange(index, "phone", e.target.value);
                            }}
                            fullWidth
                            inputProps={{ maxLength: 15 }}
                            placeholder="(11) 99999-9999"
                            autoComplete="new-password"
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                ))
              )}
            </Grid>
          </TabPanel>

          {/* Aba: Bancos */}
          <TabPanel value={tabIndex} index={4}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ typography: 'subtitle1', fontWeight: 500 }}>Bancos do Cliente</Box>
                  <AddButton onClick={handleAddBank} sx={{ width: 200 }}>
                    <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
                    Adicionar Banco
                  </AddButton>
                </Box>
              </Grid>

              {banks.length === 0 ? (
                <Grid item xs={12}>
                  <Box 
                    sx={{ 
                      textAlign: 'center', 
                      py: 3, 
                      px: 3,
                      color: 'text.secondary',
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'action.hover',
                    }}
                  >
                    Nenhum banco cadastrado. Clique em "Adicionar Banco" para adicionar.
                  </Box>
                </Grid>
              ) : (
                banks.map((bank, index) => (
                  <Grid item xs={12} key={index}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        p: 1.5,
                        position: 'relative',
                        backgroundColor: 'background.paper',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        '&:hover': {
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <Button
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          minWidth: 'auto',
                          width: 28,
                          height: 28,
                          p: 0,
                          zIndex: 10,
                          color: 'error.main',
                          backgroundColor: 'transparent',
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'error.dark',
                          },
                        }}
                        onClick={() => handleRemoveBank(index)}
                        size="small"
                        title="Remover banco"
                      >
                        <IconX size="0.9rem" />
                      </Button>

                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <FormControl fullWidth size="small" error={!!bankErrors[index]} sx={{ mt: 0 }}>
                            <InputLabel id={`bank-select-label-${index}`}>Banco</InputLabel>
                            <Select
                              labelId={`bank-select-label-${index}`}
                              id={`bank-select-${index}`}
                              value={
                                bank.bank && typeof bank.bank === 'object' && bank.bank.id
                                  ? bank.bank.id
                                  : bank.bank && typeof bank.bank === 'number'
                                  ? bank.bank
                                  : ""
                              }
                              label="Banco"
                              onChange={(e) => {
                                const selectedBank = banksList.find((b) => b.id === e.target.value);
                                handleBankChange(index, "bank", selectedBank || null);
                              }}
                              autoComplete="off"
                            >
                              <MenuItem value="">
                                <em>Selecione um banco</em>
                              </MenuItem>
                              {banksList.map((bankOption) => (
                                <MenuItem key={bankOption.id} value={bankOption.id}>
                                  {bankOption.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {bankErrors[index] && (
                              <FormHelperText>{bankErrors[index]}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                ))
              )}
            </Grid>
          </TabPanel>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openFilesModal} onClose={handleCloseFilesModal} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <IconPhoto stroke={1.5} size="1.5rem" color="inherit" />
          Arquivos do cliente: {customerForFiles?.name ?? ""}
        </DialogTitle>
        <DialogContent dividers>
          {filesLoading ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography color="text.secondary">Carregando arquivos...</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Lista de arquivos */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: "text.secondary" }}>
                  Arquivos anexados
                </Typography>
                {customerFilesList.length === 0 ? (
                  <Box sx={{ py: 3, textAlign: "center", border: "1px dashed", borderColor: "divider", borderRadius: 1 }}>
                    <IconFileText stroke={1} size={40} style={{ opacity: 0.4, marginBottom: 8 }} />
                    <Typography variant="body2" color="text.secondary">
                      Nenhum arquivo incluído.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Use o formulário abaixo para adicionar imagens ou PDFs.
                    </Typography>
                  </Box>
                ) : (
                  <List dense disablePadding sx={{ "& > * + *": { mt: 0.5 } }}>
                    {customerFilesList.map((f) => (
                      <ListItem
                        key={f.id}
                        sx={{
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                        secondaryAction={
                          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                            {f.drive_file_id && (
                              <Tooltip title="Download">
                                <IconButton size="small" onClick={() => handleDownloadFile(f.id)} sx={{ color: "secondary.main" }}>
                                  <IconDownload stroke={1.5} size="1rem" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Excluir">
                              <DeleteButton onClick={() => handleDeleteFile(f.id)}>
                                <IconTrash stroke={1.5} size="1rem" />
                              </DeleteButton>
                            </Tooltip>
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={f.title}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>

              {/* Formulário de upload */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50", borderLeft: "4px solid", borderLeftColor: "secondary.main" }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <IconUpload stroke={1.5} size="1.1rem" />
                  Adicionar arquivos
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                  Arraste arquivos aqui ou clique para selecionar. Imagens (JPEG, PNG, GIF, WebP, BMP) ou PDF.
                </Typography>
                <Box
                  component="label"
                  htmlFor="customer-file-input"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOver(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOver(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOver(false);
                    handleAddFiles(e.dataTransfer?.files);
                  }}
                  sx={{
                    border: "2px dashed",
                    borderColor: dragOver ? "secondary.main" : "divider",
                    borderRadius: 1,
                    p: 2,
                    textAlign: "center",
                    bgcolor: dragOver ? "action.hover" : "transparent",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "block",
                    "&:hover": { borderColor: "secondary.light", bgcolor: "action.hover" },
                  }}
                >
                  <input
                    id="customer-file-input"
                    type="file"
                    multiple
                    accept=".pdf,image/jpeg,image/png,image/gif,image/webp,image/bmp"
                    onChange={(e) => {
                      handleAddFiles(e.target.files);
                      e.target.value = "";
                    }}
                    style={{ display: "none" }}
                  />
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    component="span"
                    startIcon={<IconUpload stroke={1.5} size="1rem" />}
                  >
                    Selecionar arquivos
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    ou arraste e solte aqui
                  </Typography>
                </Box>
                {newFileFiles.length > 0 && (
                  <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                    {newFileFiles.map((item, idx) => (
                      <Box
                        key={`${item.file.name}-${idx}`}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          p: 1.5,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                            {item.file.name}
                          </Typography>
                          <TextField
                            label="Título"
                            value={item.title}
                            onChange={(e) => handleUpdateFileTitle(idx, e.target.value)}
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 100 }}
                            placeholder="Ex: RG, CNH, comprovante..."
                          />
                        </Box>
                        <IconButton size="small" onClick={() => handleRemoveFileToUpload(idx)} color="error" sx={{ mt: 0.5 }}>
                          <IconX stroke={1.5} size="1rem" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
                <Grid container spacing={2} alignItems="flex-end" sx={{ mt: 1.5 }}>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleUploadFile}
                      disabled={uploadLoading || newFileFiles.length === 0}
                      size="small"
                      fullWidth
                      startIcon={uploadLoading ? null : <IconPlus stroke={1.5} size="1rem" />}
                    >
                      {uploadLoading ? "Enviando..." : newFileFiles.length > 0 ? `Incluir (${newFileFiles.length})` : "Incluir"}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={handleCloseFilesModal} variant="outlined" color="secondary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Importação CSV */}
      <Dialog open={openImportModal} onClose={handleCloseImportModal} maxWidth="md" fullWidth>
        <DialogTitle>Importar Clientes via CSV</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Faça upload de um arquivo CSV contendo as colunas: <strong>Nome</strong> e <strong>CPF/CNPJ</strong> (ambos obrigatórios),{" "}
              <strong>Telefone</strong>, <strong>E-mail</strong>,{" "}
              <strong>Cep</strong>, <strong>Logradouro</strong>, <strong>Número</strong>,{" "}
              <strong>Complemento</strong>, <strong>Bairro</strong>, <strong>Cidade</strong>,{" "}
              <strong>UF</strong>. Aceita delimitador vírgula ou ponto e vírgula.
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                onClick={handleDownloadTemplate}
                startIcon={<IconDownload />}
                sx={{ mr: 2 }}
              >
                Baixar Template
              </Button>
              <Typography variant="caption" color="textSecondary">
                Baixe o template para verificar o formato correto do arquivo
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              ref={fileInputRef}
              style={{ display: "none" }}
            />

            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              startIcon={<IconUpload />}
              sx={{ mb: 2 }}
            >
              Selecionar Arquivo CSV
            </Button>

            {selectedFile && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Arquivo selecionado: <strong>{selectedFile.name}</strong>
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Tamanho: {(selectedFile.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            )}

            {importing && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Importando arquivo...
                </Typography>
                <LinearProgress />
              </Box>
            )}

            {importResult && (
              <Box sx={{ mt: 2 }}>
                {importResult.success ? (
                  <Alert severity={importResult.total_errors > 0 ? "warning" : "success"}>
                    <Typography variant="body2">
                      <strong>Importação concluída!</strong>
                    </Typography>
                    <Typography variant="body2">
                      • Criados: {importResult.created} clientes
                    </Typography>
                    <Typography variant="body2">
                      • Atualizados: {importResult.updated} clientes
                    </Typography>
                    {importResult.skipped > 0 && (
                      <Typography variant="body2">
                        • Ignorados: {importResult.skipped} linhas
                      </Typography>
                    )}
                    {importResult.total_errors > 0 && (
                      <Typography variant="body2">
                        • Erros: {importResult.total_errors} linhas
                      </Typography>
                    )}
                  </Alert>
                ) : (
                  <Alert severity="error">
                    <Typography variant="body2">
                      <strong>Erro ao importar o arquivo:</strong>
                    </Typography>
                    <Typography variant="body2">{importResult.error}</Typography>
                    {importResult.found_headers && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" display="block">
                          <strong>Cabeçalhos encontrados:</strong>{" "}
                          {importResult.found_headers.join(", ")}
                        </Typography>
                      </Box>
                    )}
                  </Alert>
                )}

                {importResult.errors && importResult.errors.length > 0 && (
                  <Box sx={{ mt: 2, maxHeight: 200, overflow: "auto" }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Detalhes dos erros encontrados:</strong>
                    </Typography>
                    {importResult.errors.map((error, index) => (
                      <Typography key={index} variant="caption" color="error" display="block">
                        {error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportModal} color="secondary">
            Fechar
          </Button>
          <Button
            onClick={handleImportCSV}
            color="primary"
            disabled={!selectedFile || importing}
            startIcon={importing ? null : <IconUpload />}
          >
            {importing ? "Importando..." : "Importar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CustomersList;
